/**
 * Фото профиля без отдельного раздела (изменение v1.15).
 *
 * Раньше фото менялись на своём экране U-04 «Фотографии»: из настроек и из
 * профиля туда вёл отдельный переход. Теперь всё делается на месте, как в
 * Telegram: «Изменить фото» в настройках и «Выбрать фото» в редактировании
 * открывают выбор источника (U-05), затем кадр и миниатюру (U-16), затем
 * загрузку; просмотр, порядок и удаление — в просмотрщике (U-17).
 *
 * Новое фото ставится либо главным («Изменить фото» — как Set New Photo в
 * Telegram), либо в конец («Добавить» в ленте фото редактирования).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { useMediaUpload, type MediaUploadState } from '@features/media/use-media-upload'
import { api, idempotency, unwrap, uuid } from '@shared/api/client'
import { ApiError } from '@shared/api/errors'
import type { ProfilePhoto } from '@shared/api/types'
import { acceptAttribute } from '@shared/media/limits'
import { useHasPointerFine } from '@shared/lib/use-media-query'
import { ActionItem, ActionList, Button, Dialog, Sheet, useToast } from '@shared/ui'

import { refreshMe } from '../../boot/refresh-me'
import { useAppStore } from '../../model/app-store'
import { LazyPhotoCropper, LazyPhotoViewer } from './lazy-photo'
import styles from './photos.module.css'

/** Куда встаёт новое фото: главным или в конец ленты. */
export type PhotoTarget = 'main' | 'add'

export interface ProfilePhotos {
  photos: readonly ProfilePhoto[]
  max: number
  canAdd: boolean
  upload: MediaUploadState
  cancelUpload: () => void
  busy: boolean
  /** U-05: выбор источника для нового фото. */
  choose: (target: PhotoTarget) => void
  /** U-17: просмотр; `editing` — с действиями над снимком. */
  openViewer: (index: number, editing?: boolean) => void
  /** Скрытые поля выбора файла, лист источника, кадр, просмотр и подтверждения. */
  ui: ReactNode
}

function describePhotoError(error: unknown): string {
  if (!(error instanceof ApiError)) return 'Не удалось добавить фото'

  switch (error.code) {
    case 'photo_limit_reached':
      return 'Достигнут предел количества фотографий'
    case 'media_not_ready':
      return 'Фото ещё обрабатывается, попробуйте через секунду'
    case 'media_invalid_purpose':
      return 'Этот файл нельзя использовать как фото профиля'
    default:
      return 'Не удалось добавить фото'
  }
}

export function useProfilePhotos(): ProfilePhotos {
  const toast = useToast()
  const me = useAppStore((s) => s.me)
  const config = useAppStore((s) => s.config)
  const hasPointerFine = useHasPointerFine()

  const galleryRef = useRef<HTMLInputElement>(null)
  const cameraRef = useRef<HTMLInputElement>(null)
  const targetRef = useRef<PhotoTarget>('add')
  const [sourceOpen, setSourceOpen] = useState(false)
  const [cropFile, setCropFile] = useState<File | null>(null)
  const [viewer, setViewer] = useState<{ index: number; editing: boolean } | null>(null)
  const [pendingDelete, setPendingDelete] = useState<ProfilePhoto | null>(null)
  const [busy, setBusy] = useState(false)

  const upload = useMediaUpload({ config, purpose: 'profile_photo', accountStatus: me?.user.status })

  const photos = me?.profile.photos ?? []
  const max = config?.limits.photos_max ?? 9
  const canAdd = photos.length < max
  const minSide = upload.limit?.min_side_px ?? 0

  // U-14: причина ошибки загрузки — сразу и понятными словами, где бы ни меняли фото
  const uploadError = upload.state.error
  useEffect(() => {
    if (uploadError) toast.show(uploadError, { tone: 'danger' })
    // toast — стабильный объект провайдера
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadError])

  const choose = (target: PhotoTarget): void => {
    if (!canAdd) {
      toast.show(`Уже ${max} фото — удалите одно, чтобы добавить новое`)
      return
    }
    targetRef.current = target
    setSourceOpen(true)
  }

  const pick = (input: HTMLInputElement | null): void => {
    setSourceOpen(false)
    input?.click()
  }

  const save = async (file: File): Promise<void> => {
    const mediaId = await upload.uploadFile(file)
    if (!mediaId) return

    try {
      const created = unwrap(
        await api.POST('/me/profile/photos', {
          body: { media_id: mediaId } as never,
          ...idempotency(uuid()),
        }),
      )
      // «Изменить фото» — новое становится главным, остальные сохраняют порядок
      if (targetRef.current === 'main' && photos.length) {
        await api.PUT('/me/profile/photos/order', {
          body: { photo_ids: [created.id, ...photos.map((p) => p.id)] } as never,
        })
      }
      await refreshMe()
      upload.reset()
      toast.show(targetRef.current === 'main' ? 'Фото профиля обновлено' : 'Фото добавлено')
    } catch (error) {
      upload.reset()
      toast.show(describePhotoError(error), { tone: 'danger' })
    }
  }

  const reorder = async (ids: string[]): Promise<void> => {
    setBusy(true)
    try {
      await api.PUT('/me/profile/photos/order', { body: { photo_ids: ids } as never })
      await refreshMe()
    } catch {
      toast.show('Не удалось изменить порядок', { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const makeMain = (index: number): void => {
    const photo = photos[index]
    if (!photo || index === 0) return
    void reorder([photo.id, ...photos.filter((p) => p.id !== photo.id).map((p) => p.id)]).then(() =>
      toast.show('Теперь это главное фото'),
    )
  }

  const move = (index: number, direction: -1 | 1): void => {
    const target = index + direction
    if (target < 0 || target >= photos.length) return
    const ids = photos.map((p) => p.id)
    const a = ids[index]!
    ids[index] = ids[target]!
    ids[target] = a
    void reorder(ids)
  }

  const deletePhoto = async (photo: ProfilePhoto): Promise<void> => {
    setBusy(true)
    try {
      await api.DELETE('/me/profile/photos/{photoId}', { params: { path: { photoId: photo.id } } })
      await refreshMe()
      toast.show('Фото удалено')
    } catch {
      toast.show('Не удалось удалить фото', { tone: 'danger' })
    } finally {
      setBusy(false)
      setPendingDelete(null)
    }
  }

  const accept = acceptAttribute(upload.limit)
  const onInput = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    event.target.value = ''
    // §10.1 п. 2: отмена диалога выбора — не ошибка, молчим
    if (file) setCropFile(file)
  }

  const ui = (
    <>
      <input
        ref={galleryRef}
        type="file"
        accept={accept}
        // §11.3 п. 4: скрытое поле остаётся в дереве доступности
        aria-label="Выбрать фотографию"
        className={styles['hiddenInput']}
        onChange={onInput}
      />
      <input
        ref={cameraRef}
        type="file"
        accept={accept}
        capture="user"
        aria-label="Снять фото на камеру"
        className={styles['hiddenInput']}
        onChange={onInput}
      />

      {/* U-05: источник — как «Set New Photo» в Telegram */}
      <Sheet open={sourceOpen} onClose={() => setSourceOpen(false)} title="Фото профиля" flush>
        <ActionList>
          <ActionItem icon="image" label="Выбрать из галереи" onClick={() => pick(galleryRef.current)} />
          {/* §10.1: съёмка — на телефоне; на desktop `capture` игнорируется */}
          {!hasPointerFine ? (
            <ActionItem icon="camera" label="Снять на камеру" onClick={() => pick(cameraRef.current)} />
          ) : null}
          {targetRef.current === 'main' && photos.length ? (
            <ActionItem
              icon="eye"
              label="Открыть фото"
              onClick={() => {
                setSourceOpen(false)
                setViewer({ index: 0, editing: false })
              }}
            />
          ) : null}
          {targetRef.current === 'main' && photos[0] ? (
            <ActionItem
              icon="trash"
              label="Удалить фото"
              danger
              onClick={() => {
                setSourceOpen(false)
                setPendingDelete(photos[0] ?? null)
              }}
            />
          ) : null}
        </ActionList>
      </Sheet>

      {cropFile ? (
        <LazyPhotoCropper
          file={cropFile}
          minSide={minSide}
          onCancel={() => setCropFile(null)}
          onDone={(cropped) => {
            const source = cropped ?? cropFile
            setCropFile(null)
            void save(source)
          }}
        />
      ) : null}

      {viewer && photos.length ? (
        <LazyPhotoViewer
          photos={photos}
          startIndex={viewer.index}
          title={me?.profile.display_name}
          actions={
            viewer.editing
              ? {
                  busy,
                  onMakeMain: makeMain,
                  onMove: move,
                  onDelete: (index) => setPendingDelete(photos[index] ?? null),
                }
              : undefined
          }
          onClose={() => setViewer(null)}
        />
      ) : null}

      <Dialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Удалить фотографию?"
        description="Её больше не будет в вашей анкете."
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingDelete(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={busy}
              onClick={() => {
                if (pendingDelete) void deletePhoto(pendingDelete)
              }}
            >
              Удалить
            </Button>
          </>
        }
      />
    </>
  )

  return {
    photos,
    max,
    canAdd,
    upload: upload.state,
    cancelUpload: upload.cancel,
    busy,
    choose,
    openViewer: (index, editing = false) => setViewer({ index, editing }),
    ui,
  }
}
